import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type {
  ColDef,
  SelectionChangedEvent,
  GetRowIdParams,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useEvalSets, type EvalSetItem } from '../hooks/useEvalSets';
import { ErrorDisplay } from './ErrorDisplay';
import { Layout } from './Layout';
import {
  TimeAgoCellRenderer,
  CopyButtonCellRenderer,
} from './ag-grid/cellRenderers';
import './ag-grid/styles.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const PAGE_SIZE = 50;

function TaskNamesCellRenderer({ value }: { value: string[] }) {
  if (!value || value.length === 0) return <span>-</span>;
  const text = value.join(', ');
  const truncated = text.length > 100 ? text.slice(0, 100) + '...' : text;
  return <span title={text}>{truncated}</span>;
}

export function EvalSetList() {
  const gridRef = useRef<AgGridReact<EvalSetItem>>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [selectedEvalSets, setSelectedEvalSets] = useState<EvalSetItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const { evalSets, isLoading, error, total, page, setPage, setSearch } =
    useEvalSets({
      page: currentPage,
      limit: PAGE_SIZE,
      search: searchQuery,
    });

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const viewSamplesHref = useMemo(() => {
    if (selectedEvalSets.length === 0) return undefined;
    const combinedIds = selectedEvalSets.map(es => es.eval_set_id).join(',');
    const base = `/eval-set/${encodeURIComponent(combinedIds)}`;
    return selectedEvalSets.length === 1 ? base : `${base}/inspect#/samples/`;
  }, [selectedEvalSets]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      setPage(newPage);
      // Clear selection when changing pages
      setSelectedEvalSets([]);
      if (gridRef.current?.api) {
        gridRef.current.api.deselectAll();
      }
    },
    [setPage]
  );

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const displayPage = page || currentPage;

  const columnDefs = useMemo<ColDef<EvalSetItem>[]>(
    () => [
      {
        field: 'eval_set_id',
        headerName: 'Eval Set ID',
        flex: 1,
        minWidth: 200,
        cellRenderer: CopyButtonCellRenderer,
      },
      {
        field: 'task_names',
        headerName: 'Task Names',
        flex: 1,
        minWidth: 200,
        cellRenderer: TaskNamesCellRenderer,
        valueFormatter: params => params.value?.join(', ') || '-',
        sortable: false,
      },
      {
        field: 'created_by',
        headerName: 'Created By',
        width: 150,
        valueFormatter: params => params.value || '-',
      },
      {
        field: 'eval_count',
        headerName: 'Eval Count',
        width: 110,
      },
      {
        field: 'latest_eval_created_at',
        headerName: 'Latest Activity',
        width: 150,
        cellRenderer: TimeAgoCellRenderer,
      },
      {
        headerName: '',
        width: 140,
        sortable: false,
        cellRenderer: (params: { data: EvalSetItem }) => (
          <span className="flex gap-3 text-xs font-medium">
            <a
              href={`/search?eval_set=${encodeURIComponent(params.data.eval_set_id)}`}
              className="text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              Search
            </a>
            <a
              href={`/launch?clone=${encodeURIComponent(params.data.eval_set_id)}`}
              className="text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              Clone
            </a>
          </span>
        ),
      },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef<EvalSetItem>>(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
    }),
    []
  );

  const getRowId = useCallback(
    (params: GetRowIdParams<EvalSetItem>) => params.data.eval_set_id,
    []
  );

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<EvalSetItem>) => {
      const selected = event.api.getSelectedRows();
      setSelectedEvalSets(selected);
    },
    []
  );

  if (error) {
    return (
      <Layout>
        <ErrorDisplay message={error.toString()} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="h-full flex flex-col overflow-hidden bg-white dark:bg-gray-900">
        <div className="bg-gray-50 dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 px-4 py-2 shrink-0">
          <form
            onSubmit={e => e.preventDefault()}
            className="flex items-center gap-3"
          >
            <div className="flex-1 relative max-w-md">
              <input
                ref={searchInputRef}
                type="search"
                placeholder="Search eval sets..."
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  setSearch(e.target.value);
                  setCurrentPage(1);
                  setPage(1);
                  setSelectedEvalSets([]);
                  if (gridRef.current?.api) {
                    gridRef.current.api.deselectAll();
                  }
                }}
                className="w-full h-8 px-3 text-sm rounded border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-brand-600 focus:border-brand-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:ring-brand-300 dark:focus:border-brand-300"
              />
              {isLoading && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <div className="animate-spin h-4 w-4 border-2 border-gray-300 dark:border-gray-700 border-t-brand-600 dark:border-t-brand-300 rounded-full"></div>
                </div>
              )}
            </div>
            {viewSamplesHref ? (
              <a
                href={viewSamplesHref}
                className="h-8 px-4 text-sm font-medium rounded transition-colors whitespace-nowrap inline-flex items-center no-underline text-white bg-brand-600 hover:bg-brand-700 dark:bg-brand-500 dark:hover:bg-brand-400"
              >
                View Samples ({selectedEvalSets.length})
              </a>
            ) : (
              <span className="h-8 px-4 text-sm font-medium rounded whitespace-nowrap inline-flex items-center cursor-not-allowed bg-gray-200 text-gray-400 dark:bg-gray-800 dark:text-gray-600">
                View Samples
              </span>
            )}
            <a
              href="/launch"
              className="h-8 px-4 text-sm font-medium rounded transition-colors whitespace-nowrap inline-flex items-center no-underline text-white bg-brand-600 hover:bg-brand-700 dark:bg-brand-500 dark:hover:bg-brand-400"
            >
              New Eval Set
            </a>
          </form>
        </div>

        {/* AG Grid */}
        <div className="flex-1 overflow-hidden relative">
          {isLoading ? (
            <div className="p-4">
              <div className="space-y-2">
                {Array.from({ length: 15 }).map((_, i) => (
                  <div key={i} className="flex gap-4 animate-pulse">
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-8"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-48"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-48"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-32"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-24"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded flex-1"></div>
                  </div>
                ))}
              </div>
            </div>
          ) : evalSets.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              {searchQuery
                ? `No eval sets found matching "${searchQuery}"`
                : 'No eval sets found'}
            </div>
          ) : (
            <div className="ag-theme-quartz h-full w-full">
              <AgGridReact<EvalSetItem>
                ref={gridRef}
                rowData={evalSets}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowId={getRowId}
                rowSelection={{
                  mode: 'multiRow',
                  headerCheckbox: true,
                  checkboxes: true,
                  enableClickSelection: true,
                }}
                onSelectionChanged={onSelectionChanged}
                animateRows={false}
                suppressCellFocus={true}
                domLayout="normal"
              />
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800 px-4 py-2 flex items-center justify-between shrink-0">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {(displayPage - 1) * PAGE_SIZE + 1}–
              {Math.min(displayPage * PAGE_SIZE, total)} of {total}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(displayPage - 1)}
                disabled={displayPage === 1 || isLoading}
                className={`h-7 px-3 text-xs font-medium rounded ${
                  displayPage === 1 || isLoading
                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}
              >
                ← Prev
              </button>
              <span className="px-2 text-xs text-gray-500 dark:text-gray-400">
                {displayPage} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(displayPage + 1)}
                disabled={displayPage === totalPages || isLoading}
                className={`h-7 px-3 text-xs font-medium rounded ${
                  displayPage === totalPages || isLoading
                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
